import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Button,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import {
  AdminCouponUsageReport,
  getAdminCouponUsageReport,
} from "../api/adminCouponsApi";

type Props = {
  open: boolean;
  couponId: number | null;
  onClose: () => void;
};

const CouponUsageDialog: React.FC<Props> = ({ open, couponId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<AdminCouponUsageReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!open || !couponId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getAdminCouponUsageReport(couponId);
      setReport(data);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Failed to load coupon usage report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, couponId]);

  const formatMoney = (value: number | string | null | undefined) =>
    `$${Number(value || 0).toFixed(2)}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Coupon Usage Report
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Stack py={6} alignItems="center">
            <CircularProgress />
          </Stack>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : !report ? (
          <Typography color="text.secondary">No report available.</Typography>
        ) : (
          <Stack spacing={3}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="h6" fontWeight={800}>
                  {report.coupon.code}
                </Typography>

                <Chip
                  size="small"
                  label={report.coupon.is_active ? "Active" : "Inactive"}
                  color={report.coupon.is_active ? "success" : "default"}
                />

                {report.coupon.usage_limit != null && (
                  <Chip
                    size="small"
                    label={`Usage limit: ${report.coupon.usage_limit}`}
                  />
                )}

                {report.coupon.usage_per_user_limit != null && (
                  <Chip
                    size="small"
                    label={`Per user limit: ${report.coupon.usage_per_user_limit}`}
                  />
                )}
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2} mt={1}>
                <Typography variant="body2" color="text.secondary">
                  Total uses: <b>{report.summary.total_uses}</b>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Unique emails: <b>{report.summary.unique_emails}</b>
                </Typography>
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
                Usage by Email
              </Typography>

              {report.by_email.length === 0 ? (
                <Typography color="text.secondary">No email usage found.</Typography>
              ) : (
                <Stack spacing={1.25}>
                  {report.by_email.map((row) => (
                    <Paper key={row.customer_email} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={2}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", md: "center" }}
                      >
                        <Box>
                          <Typography fontWeight={700}>{row.customer_email}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Last used: {dayjs(row.last_used_at).format("YYYY-MM-DD HH:mm")}
                          </Typography>
                        </Box>

                        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                          <Typography variant="body2" color="text.secondary">
                            Uses: <b>{row.total_uses}</b>
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total discount: <b>{formatMoney(row.total_discount_amount)}</b>
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Orders: <b>{row.order_ids.length}</b>
                          </Typography>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
                Usage History
              </Typography>

              {report.usage_rows.length === 0 ? (
                <Typography color="text.secondary">No usage recorded yet.</Typography>
              ) : (
                <Stack spacing={1.25}>
                  {report.usage_rows.map((row) => {
                    const displayEmail =
                      row.customer_email || row.order_email || "—";

                                        const displayName = String((row as any).order_customer_name || "").trim();

                    return (
                      <Paper key={row.id} variant="outlined" sx={{ p: 1.5 }}>
                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          spacing={2}
                          justifyContent="space-between"
                          alignItems={{ xs: "flex-start", md: "center" }}
                        >
                          <Box>
                            <Typography fontWeight={700}>{displayEmail}</Typography>
                            {displayName ? (
                              <Typography variant="body2" color="text.secondary">
                                {displayName}
                              </Typography>
                            ) : null}
                            <Typography variant="body2" color="text.secondary">
                              Used: {dayjs(row.used_at).format("YYYY-MM-DD HH:mm")}
                            </Typography>
                          </Box>

                          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                            <Typography variant="body2" color="text.secondary">
                              Discount: <b>{formatMoney(row.discount_amount)}</b>
                            </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                              Order ID: <b>{row.order_id ?? "—"}</b>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Order #: <b>{(row as any).order_number ?? "—"}</b>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Customer ID: <b>{row.customer_id ?? "—"}</b>
                            </Typography>
                          </Stack>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CouponUsageDialog;